import React, { useEffect, useRef } from "react";
import { InteractionType } from "@azure/msal-browser";
import type { MsalAuthenticationResult } from "@azure/msal-react";
import { Button, CircularProgress, Grid, Typography } from "@mui/material";
import { isInteractionRequiredError } from "./interactionRequired";

const centeredContainerSx = {
  minHeight: "100vh",
};

const centeredContentSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

export function MsalAuthErrorFallback({ error, login }: MsalAuthenticationResult) {
  const hasAttemptedRedirect = useRef(false);
  const isRecoverable = isInteractionRequiredError(error);

  useEffect(() => {
    if (isRecoverable && !hasAttemptedRedirect.current) {
      hasAttemptedRedirect.current = true;
      login(InteractionType.Redirect).catch(() => {
        // If the redirect fails (e.g. interaction already in progress),
        // the error UI will remain visible — which is the correct fallback.
      });
    }
  }, [isRecoverable, login]);

  if (isRecoverable) {
    return (
      <Grid
        container
        spacing={0}
        sx={{
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          ...centeredContainerSx,
        }}
      >
        <Grid sx={centeredContentSx} size={3}>
          <CircularProgress />
          <p>Refreshing your session...</p>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      spacing={0}
      sx={{
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        ...centeredContainerSx,
      }}
    >
      <Grid sx={centeredContentSx} size={3}>
        <Typography variant="h6" gutterBottom>
          Authentication Error
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 2,
          }}
        >
          {error?.errorMessage || "An unexpected authentication error occurred."}
        </Typography>
        <Button variant="contained" onClick={() => login(InteractionType.Redirect)}>
          Try Again
        </Button>
      </Grid>
    </Grid>
  );
}

export function MsalAuthLoading() {
  return (
    <Grid
      container
      spacing={0}
      sx={{
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        ...centeredContainerSx,
      }}
    >
      <Grid sx={centeredContentSx} size={3}>
        <CircularProgress />
        <p>Loading...</p>
      </Grid>
    </Grid>
  );
}
